// Etablissement Service - Manages establishment settings and campuses

import { apiGet, apiPost, apiPatch, apiDelete, apiPostMultipart } from '@/utils/api';

export enum SigleType {
  COMMON = 'COMMON',
  PER_CAMPUS = 'PER_CAMPUS',
}

export interface Campus {
  id: string;
  tenantId: string;
  name: string;
  sigle?: string;
  address: string;
  city: string;
  postalCode: string;
  email: string;
  receptionPhoneCountry: string;
  receptionPhoneNumber: string;
  isMainCampus: boolean;
  isActive: boolean;
  sortOrder: number;
  wasAutoCreated?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EtablissementSettings {
  id: string;
  name: string;
  sigleType: SigleType;
  commonSigle?: string;
  logoUrl?: string;
  isUniversity: boolean;
  primaryColor?: string;
  secondaryColor?: string;
  campusCount: number;
  campuses: Campus[];
}

export interface UpdateBrandColorsDto {
  primaryColor: string;
  secondaryColor: string;
}

export interface UpdateEtablissementDto {
  name: string;
  sigleType: SigleType;
  commonSigle?: string;
  isUniversity?: boolean;
}

export interface CreateCampusDto {
  name: string;
  sigle?: string;
  address: string;
  city: string;
  postalCode: string;
  email: string;
  receptionPhoneCountry?: string;
  receptionPhoneNumber: string;
  isMainCampus?: boolean;
}

export interface UpdateCampusDto {
  name?: string;
  sigle?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  email?: string;
  receptionPhoneCountry?: string;
  receptionPhoneNumber?: string;
  isMainCampus?: boolean;
  isActive?: boolean;
}

// Fetch establishment settings (includes campuses)
// Note: token parameter kept for backward compatibility but not used (api helpers handle auth)
export async function fetchEtablissementSettings(_token?: string): Promise<EtablissementSettings> {
  return apiGet<EtablissementSettings>('/etablissement/settings');
}

// Update establishment settings
export async function updateEtablissementSettings(
  _token: string,
  data: UpdateEtablissementDto
): Promise<{ success: boolean; message: string; data: EtablissementSettings }> {
  return apiPatch<{ success: boolean; message: string; data: EtablissementSettings }>('/etablissement/settings', data);
}

// Fetch all campuses
export async function fetchCampuses(_token?: string, includeInactive = false): Promise<Campus[]> {
  const endpoint = includeInactive ? '/etablissement/campuses?includeInactive=true' : '/etablissement/campuses';
  return apiGet<Campus[]>(endpoint);
}

// Get a single campus
export async function fetchCampusById(_token: string, campusId: string): Promise<Campus> {
  return apiGet<Campus>(`/etablissement/campuses/${campusId}`);
}

// Create a new campus
export async function createCampus(
  _token: string,
  data: CreateCampusDto
): Promise<{ success: boolean; message: string; data: Campus }> {
  return apiPost<{ success: boolean; message: string; data: Campus }>('/etablissement/campuses', data);
}

// Update a campus
export async function updateCampus(
  _token: string,
  campusId: string,
  data: UpdateCampusDto
): Promise<{ success: boolean; message: string; data: Campus }> {
  return apiPatch<{ success: boolean; message: string; data: Campus }>(`/etablissement/campuses/${campusId}`, data);
}

// Delete a campus
export async function deleteCampus(
  _token: string,
  campusId: string
): Promise<{ success: boolean; message: string }> {
  return apiDelete<{ success: boolean; message: string }>(`/etablissement/campuses/${campusId}`);
}

// Toggle campus active status
export async function toggleCampusStatus(
  _token: string,
  campusId: string
): Promise<{ success: boolean; message: string; data: Campus }> {
  return apiPatch<{ success: boolean; message: string; data: Campus }>(`/etablissement/campuses/${campusId}/toggle`);
}

// Reorder campuses
export async function reorderCampuses(
  _token: string,
  campusIds: string[]
): Promise<{ success: boolean; message: string }> {
  return apiPatch<{ success: boolean; message: string }>('/etablissement/campuses/reorder', { campusIds });
}

// Upload establishment logo
// Recommended resolution: 200x200px (square) or 400x100px (rectangular)
// Max file size: 2MB
// Supported formats: PNG, JPG, JPEG, WebP, SVG
export async function uploadLogo(
  _token: string,
  file: File
): Promise<{ success: boolean; message: string; data: { id: string; name: string; logoUrl: string } }> {
  const formData = new FormData();
  formData.append('logo', file);

  return apiPostMultipart<{ success: boolean; message: string; data: { id: string; name: string; logoUrl: string } }>('/etablissement/logo', formData);
}

// Delete establishment logo
export async function deleteLogo(
  _token: string
): Promise<{ success: boolean; message: string }> {
  return apiDelete<{ success: boolean; message: string }>('/etablissement/logo');
}

// Update establishment brand colors (charte graphique)
export async function updateBrandColors(
  _token: string,
  data: UpdateBrandColorsDto
): Promise<{ success: boolean; message: string; data: { id: string; name: string; primaryColor: string; secondaryColor: string } }> {
  return apiPatch<{ success: boolean; message: string; data: { id: string; name: string; primaryColor: string; secondaryColor: string } }>('/etablissement/brand-colors', data);
}
